import {
  BellRing,
  CalendarDays,
  CarFront,
  ClipboardCheck,
  FileCheck2,
  Gauge,
  History,
  Landmark,
  Scale,
  CarTaxiFront,
  UserCheck,
  MessageSquareText,
  Repeat2,
  Search,
  ShieldCheck,
  Users,
  WalletCards,
  Wrench,
  BookOpenCheck,
} from "lucide-react";

const IMAGE_ROOT = "/images/features";

export const renterFeatureGroups = [
  {
    id: "discover",
    eyebrow: "Find and compare",
    title: "Start with rental businesses already using ConfirmPH.",
    description: "Browse participating rental businesses, filter the directory, and open the public information a business has chosen to publish.",
    icon: Search,
    benefits: [
      "Search by business name, city, rental type, and booking option.",
      "See business contact details, public policies, listed vehicles, and booking availability where enabled.",
      "Open the business website or ConfirmPH profile without exposing private booking data.",
    ],
    image: `${IMAGE_ROOT}/renter-discovery.webp`,
    alt: "ConfirmPH Discover Rentals page with rental business filters",
    caption: "The real Discover Rentals screen, shown with demo-safe data.",
  },
  {
    id: "confirm",
    eyebrow: "Review before pickup",
    title: "Confirm the record, not just the chat message.",
    description: "A secure confirmation link shows the vehicle, dates, service mode (self-drive or with a driver), price, any advance or downpayment, any refundable security deposit, pickup details, terms, the agreement version you are accepting, and any recorded existing damage.",
    icon: ShieldCheck,
    benefits: [
      "Open the confirmation link without creating an account.",
      "Confirm the booking or request structured changes to dates, times, location, or vehicle.",
      "Cancel an awaiting-confirmation request before it is confirmed.",
      "Review material business changes before accepting them.",
      "For self-drive, see which authorized drivers are on the booking.",
    ],
    image: `${IMAGE_ROOT}/renter-confirmation.webp`,
    alt: "ConfirmPH customer confirmation page showing booking and vehicle details",
    caption: "A real confirmation screen with the walkthrough guidance visible.",
  },
  {
    id: "booking-record",
    eyebrow: "Keep one reference",
    title: "Your booking details stay together.",
    description: "Signed-in customer accounts can see current and past bookings, status, business contact details, any advance and security-deposit status, confirmation history, and the condition record shared with them.",
    icon: FileCheck2,
    benefits: [
      "See upcoming, ongoing, completed, cancelled, and no-show bookings.",
      "Open the vehicle and customer-visible condition record from the booking.",
      "Contact the booking provider from the same record when something changes.",
      "Use Book Again after a completed rental without creating a second identity.",
    ],
    image: `${IMAGE_ROOT}/renter-booking-record.webp`,
    alt: "ConfirmPH My Booking page for a customer-confirmed rental",
    caption: "The real customer booking detail screen, populated with demo-safe data.",
  },
  {
    id: "changes",
    eyebrow: "Changes stay explicit",
    title: "See what changed before you agree.",
    description: "When a business changes a material booking detail, ConfirmPH can show the previous and updated value and ask the customer to accept or request clarification.",
    icon: Repeat2,
    benefits: [
      "Compare the previous and updated vehicle, dates, locations, price, or terms, including a switch between self-drive and with-driver, the driver-service charge, the advance or downpayment, the security-deposit amount, and material fuel, mileage or cancellation terms.",
      "Accept the change or ask the business for clarification.",
      "Confirm a business-proposed extension before the return date changes.",
      "The version you accepted earlier stays in the booking history; a change never silently inherits it.",
    ],
    image: `${IMAGE_ROOT}/renter-change-review.webp`,
    alt: "Mobile ConfirmPH change review showing previous and updated vehicles",
    caption: "The real mobile change-review card used in the incident walkthrough.",
  },
  {
    id: "review",
    eyebrow: "After the rental",
    title: "Leave a booking-linked review.",
    description: "After you return the vehicle, the business inspects it and records any post-rental assessment, then the security-deposit refund or application, any dispute or settlement if needed, and closure. Damage recorded at return does not by itself mean you are liable. Review invitations are tied to completed bookings, so random visitors cannot review a business they did not rent from.",
    icon: MessageSquareText,
    benefits: [
      "Follow return, inspection, security-deposit status and closure on the booking.",
      "Respond to or dispute a post-rental charge or deposit application.",
      "Rate and review through an invite linked to the completed booking.",
      "See the review state from My Bookings.",
      "Return to the business profile or start a repeat booking.",
      "Request an export or deletion of customer data through the Privacy Center.",
    ],
    image: `${IMAGE_ROOT}/renter-verified-review.webp`,
    alt: "ConfirmPH booking-linked review form",
    caption: "The real invite-only review form, shown with demo-safe data.",
  },
];

export const businessFeatureGroups = [
  {
    id: "intake",
    eyebrow: "1. Capture the booking",
    title: "Turn conversations into structured booking records.",
    description: "Record inquiries from Messenger, WhatsApp, SMS, phone, walk-in, or another source without pretending ConfirmPH reads private conversations automatically.",
    icon: ClipboardCheck,
    benefits: [
      "Use Booking Intake or the offline-capable Notepad to capture key details.",
      "Convert a local note into a draft only when online and signed in.",
      "Surface possible duplicate inquiries and possible returning customers before creating another record.",
      "Keep the original intake source on the booking for later reporting.",
    ],
    image: `${IMAGE_ROOT}/business-booking-intake.webp`,
    alt: "ConfirmPH Notepad converting a Messenger inquiry into a booking draft",
    caption: "The real Local Mode Notepad inside a public demo workflow.",
  },
  {
    id: "confirmation",
    eyebrow: "2. Confirm with the customer",
    title: "Send one clear record for the customer to review.",
    description: "Create confirmation versions, record the customer's response, and keep material changes explicit instead of burying them in a message thread.",
    icon: FileCheck2,
    benefits: [
      "Generate secure confirmation links with expiry and single-use protections.",
      "Track awaiting, confirmed, change-requested, cancelled, and delivery-failed states.",
      "Show customer-visible terms, deposit status, vehicle details, and existing damage.",
      "Resend updated confirmation when material booking details change.",
      "Generate a booking-specific agreement version; earlier acceptances stay preserved in the history.",
    ],
    image: `${IMAGE_ROOT}/renter-confirmation.webp`,
    alt: "Customer confirmation page generated from a ConfirmPH business booking",
    caption: "Businesses and renters reference the same booking record.",
  },
  {
    id: "schedule",
    eyebrow: "3. Protect the schedule",
    title: "Catch overlap, readiness, and turnaround risk before pickup.",
    description: "The Fleet Calendar and shared conflict service use bookings and vehicle readiness already recorded in ConfirmPH.",
    icon: CalendarDays,
    benefits: [
      "Block critical overlaps for the same vehicle and date range.",
      "Surface tight buffers between return and the next pickup.",
      "Show Available, In Use, Needs Inspection, Maintenance, Damaged, and Unavailable states.",
      "Suggest suitable replacement vehicles through the existing availability rules.",
      "Check driver availability and qualification for with-driver bookings, and authorized-driver eligibility for self-drive bookings.",
    ],
    image: `${IMAGE_ROOT}/business-warning-center.webp`,
    alt: "ConfirmPH Warning Center with return, deposit, and inspection risks",
    caption: "Operational warnings are based on records already in ConfirmPH.",
  },
  {
    id: "fleet",
    eyebrow: "4. Manage the fleet",
    title: "Give every vehicle an operational history.",
    description: "Manage vehicle availability, specifications, pricing, photos, documents, damage, mileage, inspections, maintenance, bookings, and internal notes from one profile.",
    icon: CarFront,
    benefits: [
      "Track odometer history and correct a missed reading with an audit record.",
      "Record maintenance due by date or mileage and keep service history.",
      "Store vehicle documents and expiry dates.",
      "Keep damage and inspection history attached to the vehicle.",
      "Track Vehicle Legal Readiness (registration and insurance evidence) and Enhanced Vehicle Safety separately from operational status; the three states are never combined.",
    ],
    image: `${IMAGE_ROOT}/business-fleet.webp`,
    alt: "ConfirmPH Vehicles page with fleet status and vehicle cards",
    caption: "The business fleet screen in the public demo environment.",
  },
  {
    id: "handover",
    eyebrow: "5. Hand over the vehicle",
    title: "Record odometer, fuel, condition, photos, and keys before release.",
    description: "The pickup handover follows the real booking workflow. Completing the handover fields alone does not release the vehicle: release can also depend on business readiness, vehicle legal and safety readiness, the customer's acceptance of the current agreement, any payment or security-deposit condition the business set, and company-driver readiness or renter-driver eligibility.",
    icon: Gauge,
    benefits: [
      "Capture odometer and fuel or charge level.",
      "Review existing damage before adding new photos.",
      "Record keys and released items, staff member, timestamp, and customer acknowledgement.",
      "Keep the booking lifecycle and vehicle readiness in the canonical services.",
    ],
    image: `${IMAGE_ROOT}/business-pickup-handover.webp`,
    alt: "ConfirmPH Pickup Handover wizard with odometer and fuel controls",
    caption: "The real pickup handover component running with demo-safe data.",
  },
  {
    id: "return",
    eyebrow: "6. Close the return safely",
    title: "A returned vehicle is checked before it becomes Available again.",
    description: "Completing a rental moves the vehicle to Needs Inspection; returned never means Available. The return inspection records damage, missing items, fuel, mileage and lateness for assessment, an insurance workflow where one applies, and then the financial settlement.",
    icon: Wrench,
    benefits: [
      "Record return odometer, fuel, cleaning, interior, exterior, photos, and acknowledgement.",
      "Keep the vehicle blocked when damage, maintenance, or an unresolved incident remains.",
      "Return a cleared vehicle to Available without changing its already assigned next booking.",
      "Preserve pickup and return condition records for later reference.",
    ],
    image: `${IMAGE_ROOT}/business-return-inspection.webp`,
    alt: "ConfirmPH return inspection form with mileage and condition checklist",
    caption: "The real return inspection component in the public demo workflow.",
  },
  {
    id: "incidents",
    eyebrow: "7. Respond when something changes",
    title: "Connect vehicle incidents to the bookings they put at risk.",
    description: "Log an incident, update vehicle readiness, surface affected future bookings, and use the existing replacement and reconfirmation path.",
    icon: BellRing,
    benefits: [
      "Record incident type, severity, vehicle, booking, notes, and attachments.",
      "Surface upcoming bookings assigned to an unavailable vehicle.",
      "Reassign a suitable vehicle or cancel without promising an unavailable replacement.",
      "Require the renter to review a material replacement before it is final.",
      "An incident or damage record does not itself decide the renter's fault or the amount owed.",
    ],
    image: `${IMAGE_ROOT}/business-incident-warning.webp`,
    alt: "ConfirmPH critical warning for an upcoming booking affected by an unavailable vehicle",
    caption: "A real affected-booking warning from the incident walkthrough.",
  },
  {
    id: "customers-staff",
    eyebrow: "8. Keep the team and customer history aligned",
    title: "Use one customer history and permission-aware staff workspace.",
    description: "Customer profiles connect booking history, rental value, incidents, deposits, changes, and factual internal notes. Employee access is enforced on the server.",
    icon: Users,
    benefits: [
      "See current and past bookings, last rental, vehicles rented, and returning-customer signals.",
      "Manage Driver Profiles with availability, vehicle and service qualification, safety and duty state, assignment and dispatch readiness.",
      "Give employees explicit permissions without removing owner or platform-admin access; driver access stays separate from Finance.",
      "Keep activity attributed to the person who performed it.",
    ],
    image: `${IMAGE_ROOT}/business-warning-center.webp`,
    alt: "ConfirmPH operational workspace with company-scoped warning records",
    caption: "Company-scoped operations remain visible to authorized staff.",
  },
  {
    id: "money-communication",
    eyebrow: "9. Record money and follow-up status",
    title: "Track rental money and communication outcomes without claiming to process the rental payment.",
    description: "ConfirmPH keeps rental payments, advances or downpayments, refundable security deposits, and refunds or applications as separate records, with financial follow-up. With Finance & Books enabled, these also become balanced accounting entries. Communication templates and reminders use configured delivery channels and log their outcomes.",
    icon: WalletCards,
    benefits: [
      "Record rental payments and advances separately from the refundable security deposit.",
      "Record a security-deposit refund, or its application to an established charge, with the reason.",
      "Show only customer-safe payment and security-deposit information in the renter portal.",
      "Prepare English or Tagalog templates with booking merge fields.",
      "See reminder and delivery outcomes while keeping payment settlement between the business and customer.",
    ],
    image: `${IMAGE_ROOT}/business-warning-center.webp`,
    alt: "ConfirmPH Warning Center showing an unresolved deposit alongside other operational items",
    caption: "Payment and security-deposit status is an operational record, not a payment gateway.",
  },
  {
    id: "public-presence",
    eyebrow: "10. Build a public presence",
    title: "Publish business information, listed vehicles, and booking-linked reviews.",
    description: "Businesses can maintain a public profile or multi-page ConfirmPH mini-site, connect an owned website, and prepare hosted or embedded booking surfaces.",
    icon: History,
    benefits: [
      "Publish contact information, policies, fleet details, FAQs, reviews, and booking options.",
      "Verify website ownership before enabling domain-bound integration settings.",
      "Use hosted booking and widget shells without exposing company IDs to the browser.",
      "Keep real booking submission disabled in the widget until the final integration path is approved.",
    ],
    image: `${IMAGE_ROOT}/renter-verified-review.webp`,
    alt: "ConfirmPH booking-linked review on a customer-facing screen",
    caption: "Public reputation starts with reviews linked to completed bookings.",
  },
  {
    id: "legal-readiness",
    eyebrow: "Business & Fleet Legal Readiness",
    title: "Keep permits, registrations and insurance evidence in view.",
    description: "Guidance, evidence upload, expiry tracking and ConfirmPH review states for the business and each vehicle. ConfirmPH review is not government certification, and a completed checklist does not mean the business is legally compliant.",
    icon: Landmark,
    benefits: [
      "Record business permits and registrations, and each vehicle's registration and insurance evidence.",
      "See expiry dates before they lapse.",
      "Evidence shows as Self-reported, Evidence uploaded, ConfirmPH reviewed, or Authority review required.",
    ],
  },
  {
    id: "drivers",
    eyebrow: "Drivers & Dispatch",
    title: "Assign company drivers who are available and qualified.",
    description: "Driver Profiles, qualification, availability, assignment and dispatch readiness for with-driver rentals.",
    icon: CarTaxiFront,
    benefits: [
      "Keep each driver's licence and qualification evidence on their profile.",
      "Assign a driver only when they pass eligibility, and see a replacement warning if they stop passing.",
      "Give drivers a restricted workspace that shows only their own trips.",
    ],
  },
  {
    id: "self-drive-drivers",
    eyebrow: "Self-Drive Authorized Drivers",
    title: "Know exactly who is allowed to drive a self-drive rental.",
    description: "Primary and additional authorized renter drivers, checked against your driver policy and insurance conditions for that booking.",
    icon: UserCheck,
    benefits: [
      "Collect licence details only from people who will actually drive.",
      "Check each driver against the booking before release.",
      "Flag unclear foreign-licence cases for review instead of approving them automatically.",
    ],
  },
  {
    id: "finance-books",
    eyebrow: "Finance & Books",
    title: "Internal, accountant-ready financial records.",
    description: "Balanced journals, reports and transaction history for rental income, advances, security deposits held, refunds and expenses. These are internal records, not automatically BIR-registered computerized books.",
    icon: BookOpenCheck,
    benefits: [
      "Every entry balances, in exact centavos.",
      "Posted entries cannot be deleted or silently edited; corrections are visible reversals.",
      "Export journals and reports for your accountant.",
    ],
  },
  {
    id: "closure",
    eyebrow: "Rental Closure",
    title: "Close the rental, not just the return.",
    description: "Return, damage and claims, security-deposit disposition, disputes and reconciliation, until the rental is closed.",
    icon: Scale,
    benefits: [
      "Open a damage case with evidence and the renter's response.",
      "Apply a security deposit only to an established charge; the renter can dispute it.",
      "See what is still outstanding, disputed or settled before closing.",
    ],
  },
];

export const featureBoundaries = [
  "ConfirmPH records and coordinates the rental. The rental business remains responsible for the vehicle, service, payment collection, and customer communication.",
  "ConfirmPH does not hold the rental business's security deposits, determine accident fault, guarantee insurance coverage, or decide final legal liability.",
  "ConfirmPH review of legal-readiness evidence is not government approval, and Finance & Books are not automatically BIR-registered books.",
  "Availability and warnings depend on the bookings, vehicle status, and operational records entered in ConfirmPH.",
  "Delivery through email or messaging providers depends on the business configuration and the provider response.",
  "The public widget remains a shell until the approved shared booking submission path is connected.",
];
